/**
 * Account System Helpers
 *
 * Utilities for creating, managing, and querying accounts and relationships.
 */

import {
  Account,
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
  AccountsState,
  RelationshipGraph,
  AccountRole,
  isLearnerAccount,
  isParentAccount,
} from '../types/accounts'
import { error as logError } from './logger'

/**
 * Generate a simple UUID v4
 */
export function generateAccountId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Create a default learner account
 */
export function createDefaultLearnerAccount(): LearnerAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'learner',
    displayName: 'Leerling',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Create a default parent account
 */
export function createDefaultParentAccount(): ParentAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'parent',
    displayName: 'Ouder',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Create a default teacher account
 */
export function createDefaultTeacherAccount(): TeacherAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'teacher',
    displayName: 'Docent',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Create a default admin account
 */
export function createDefaultAdminAccount(): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: 'Admin',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Create initial accounts state with default learner
 */
export function createInitialAccountsState(): AccountsState {
  const learner = createDefaultLearnerAccount()
  return {
    accountsById: {
      [learner.id]: learner,
    },
    activeAccountId: learner.id,
    relationships: {
      parentToLearners: {},
      learnerToParents: {},
      teacherToLearners: {},
    },
  }
}

/**
 * Create initial accounts state with learner + linked parent
 */
export function createInitialAccountsStateWithParent(): AccountsState {
  const learner = createDefaultLearnerAccount()
  const parent = createDefaultParentAccount()
  return {
    accountsById: {
      [learner.id]: learner,
      [parent.id]: parent,
    },
    activeAccountId: learner.id,
    relationships: {
      parentToLearners: {
        [parent.id]: [learner.id],
      },
      learnerToParents: {
        [learner.id]: [parent.id],
      },
      teacherToLearners: {},
    },
  }
}

/**
 * Create initial accounts state with learner + parent + teacher (all linked)
 */
export function createInitialAccountsStateWithAll(): AccountsState {
  const learner = createDefaultLearnerAccount()
  const parent = createDefaultParentAccount()
  const teacher = createDefaultTeacherAccount()
  const admin = createDefaultAdminAccount()
  return {
    accountsById: {
      [learner.id]: learner,
      [parent.id]: parent,
      [teacher.id]: teacher,
      [admin.id]: admin,
    },
    activeAccountId: learner.id,
    relationships: {
      parentToLearners: {
        [parent.id]: [learner.id],
      },
      learnerToParents: {
        [learner.id]: [parent.id],
      },
      teacherToLearners: {
        [teacher.id]: [learner.id],
      },
    },
  }
}

/**
 * Ensure an admin account exists in the accounts state.
 * Used as a post-load migration for states created before admin was added.
 */
export function ensureAdminAccount(
  accountsState: AccountsState,
): AccountsState {
  const hasAdmin = Object.values(accountsState.accountsById).some(
    (account) => account.role === 'admin',
  )
  if (hasAdmin) return accountsState

  const admin = createDefaultAdminAccount()
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [admin.id]: admin,
    },
  }
}

/**
 * Get active account from accounts state
 */
export function getActiveAccount(accountsState: AccountsState): Account | null {
  return accountsState.accountsById[accountsState.activeAccountId] || null
}

/**
 * Get account by ID
 */
export function getAccountById(
  accountsState: AccountsState,
  accountId: string,
): Account | null {
  return accountsState.accountsById[accountId] || null
}

/**
 * Update account display name
 */
export function updateAccountName(
  accountsState: AccountsState,
  accountId: string,
  newName: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) {
    logError(`Account not found: ${accountId}`)
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        displayName: newName,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update account email
 */
export function updateAccountEmail(
  accountsState: AccountsState,
  accountId: string,
  email: string,
): AccountsState {
  const account = accountsState.accountsById[accountId]
  if (!account) {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [accountId]: {
        ...account,
        email,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update teacher details
 */
export function updateTeacherDetails(
  accountsState: AccountsState,
  teacherId: string,
  updates: { schoolName?: string; classroomName?: string },
): AccountsState {
  const account = accountsState.accountsById[teacherId]
  if (!account || account.role !== 'teacher') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [teacherId]: {
        ...account,
        ...updates,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Update learner grade
 */
export function updateLearnerGrade(
  accountsState: AccountsState,
  learnerId: string,
  grade: number,
): AccountsState {
  const account = accountsState.accountsById[learnerId]
  if (!account || account.role !== 'learner') {
    return accountsState
  }
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [learnerId]: {
        ...account,
        grade,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Link parent to learner (bidirectional)
 */
export function linkParentToLearner(
  accountsState: AccountsState,
  parentId: string,
  learnerId: string,
): AccountsState {
  const parent = accountsState.accountsById[parentId]
  const learner = accountsState.accountsById[learnerId]
  if (!parent || !isParentAccount(parent)) {
    logError(`Invalid parent account: ${parentId}`)
    return accountsState
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`)
    return accountsState
  }
  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    parentToLearners: {
      ...accountsState.relationships.parentToLearners,
      [parentId]: [
        ...(accountsState.relationships.parentToLearners[parentId] || []),
        learnerId,
      ].filter((id, index, self) => self.indexOf(id) === index), // Deduplicate
    },
    learnerToParents: {
      ...accountsState.relationships.learnerToParents,
      [learnerId]: [
        ...(accountsState.relationships.learnerToParents[learnerId] || []),
        parentId,
      ].filter((id, index, self) => self.indexOf(id) === index), // Deduplicate
    },
  }
  return {
    ...accountsState,
    relationships: newRelationships,
  }
}

/**
 * Link teacher to learner
 */
export function linkTeacherToLearner(
  accountsState: AccountsState,
  teacherId: string,
  learnerId: string,
): AccountsState {
  const teacher = accountsState.accountsById[teacherId]
  const learner = accountsState.accountsById[learnerId]
  if (!teacher || teacher.role !== 'teacher') {
    logError(`Invalid teacher account: ${teacherId}`)
    return accountsState
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`)
    return accountsState
  }
  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    teacherToLearners: {
      ...accountsState.relationships.teacherToLearners,
      [teacherId]: [
        ...(accountsState.relationships.teacherToLearners[teacherId] || []),
        learnerId,
      ].filter((id, index, self) => self.indexOf(id) === index), // Deduplicate
    },
  }
  return {
    ...accountsState,
    relationships: newRelationships,
  }
}

/**
 * Get all learners linked to a parent or teacher
 */
export function getLinkedLearners(
  accountsState: AccountsState,
  parentOrTeacherId: string,
): LearnerAccount[] {
  const account = accountsState.accountsById[parentOrTeacherId]
  if (!account) return []
  let learnerIds: string[] = []
  if (account.role === 'parent') {
    learnerIds =
      accountsState.relationships.parentToLearners[parentOrTeacherId] || []
  } else if (account.role === 'teacher') {
    learnerIds =
      accountsState.relationships.teacherToLearners[parentOrTeacherId] || []
  }
  return learnerIds
    .map((id) => accountsState.accountsById[id])
    .filter((account): account is LearnerAccount =>
      account ? isLearnerAccount(account) : false,
    )
}

/**
 * Get all parents linked to a learner
 */
export function getLinkedParents(
  accountsState: AccountsState,
  learnerId: string,
): ParentAccount[] {
  const parentIds =
    accountsState.relationships.learnerToParents[learnerId] || []
  return parentIds
    .map((id) => accountsState.accountsById[id])
    .filter((account): account is ParentAccount =>
      account ? isParentAccount(account) : false,
    )
}

/**
 * Validate accounts state structure
 */
export function validateAccountsState(state: any): state is AccountsState {
  if (!state || typeof state !== 'object') return false
  if (!state.accountsById || typeof state.accountsById !== 'object')
    return false
  if (!state.activeAccountId || typeof state.activeAccountId !== 'string')
    return false
  if (!state.relationships || typeof state.relationships !== 'object')
    return false

  // Check if active account exists
  const activeAccount = state.accountsById[state.activeAccountId]
  if (!activeAccount) return false

  // Validate relationships structure
  const rel = state.relationships
  if (!rel.parentToLearners || !rel.learnerToParents || !rel.teacherToLearners)
    return false
  return true
}

/**
 * Switch active account
 */
export function switchActiveAccount(
  accountsState: AccountsState,
  newAccountId: string,
): AccountsState {
  const account = accountsState.accountsById[newAccountId]
  if (!account) {
    logError(`Cannot switch to non-existent account: ${newAccountId}`)
    return accountsState
  }
  return {
    ...accountsState,
    activeAccountId: newAccountId,
    accountsById: {
      ...accountsState.accountsById,
      [newAccountId]: {
        ...account,
        lastActive: Date.now(),
      },
    },
  }
}

/**
 * Get learner account for active context
 * - If active account is learner: return it
 * - If active account is parent/teacher: return first linked learner
 */
export function getContextLearnerAccount(
  accountsState: AccountsState,
): LearnerAccount | null {
  const activeAccount = getActiveAccount(accountsState)
  if (!activeAccount) return null
  if (isLearnerAccount(activeAccount)) {
    return activeAccount
  }
  if (isParentAccount(activeAccount)) {
    const linkedLearners = getLinkedLearners(accountsState, activeAccount.id)
    return linkedLearners[0] || null
  }

  // Teacher: return first linked learner
  const learnerIds =
    accountsState.relationships.teacherToLearners[activeAccount.id] || []
  const firstLearnerId = learnerIds[0]
  if (!firstLearnerId) return null
  const learner = accountsState.accountsById[firstLearnerId]
  return learner && isLearnerAccount(learner) ? learner : null
}
